/**
 * Report API Service
 * Handles all API calls for report generation and editing
 */

import { getAuth } from 'firebase/auth';

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8080/api'
  : 'https://lm-reports-889856480589.us-central1.run.app/api';

class ReportApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    this.details = data?.details || null;
  }
}

async function getAuthHeaders() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new ReportApiError('User not authenticated', 401);
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

async function apiFetch(url, options) {
  try {
    return await fetch(url, options);
  } catch (err) {
    throw new ReportApiError(
      'Unable to reach the server. Check your internet connection and try again.',
      0,
      null
    );
  }
}

async function handleResponse(response) {
  let data;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new ReportApiError(
        `Request failed (${response.status})`,
        response.status,
        null
      );
    }
    throw new ReportApiError('Invalid response from server', response.status, null);
  }
  if (!response.ok) {
    throw new ReportApiError(
      data.error || 'API request failed',
      response.status,
      data
    );
  }
  return data;
}

/**
 * Generate a new draft report
 * @param {Object} params - Parameters for report generation
 * @returns {Promise<Object>} Draft report with editable sections
 */
export async function generateDraft(params) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/generateDraft`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  return handleResponse(response);
}

/**
 * Get draft state by ID
 * @param {string} draftId - Draft ID
 * @returns {Promise<Object>} Draft state
 */
export async function getDraft(draftId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts/${draftId}`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * Update section content
 * @param {string} draftId - Draft ID
 * @param {string} sectionId - Section ID
 * @param {Object} content - New content for the section
 * @returns {Promise<Object>} Updated section
 */
export async function updateSection(draftId, sectionId, content) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/sections/${sectionId}`,
    {
      method: 'PATCH',
      headers,
      // Backend expects { content: { ...fields } } format
      body: JSON.stringify({ content }),
    }
  );
  return handleResponse(response);
}

/**
 * Toggle section inclusion
 * @param {string} draftId - Draft ID
 * @param {string} sectionId - Section ID
 * @param {boolean} included - Include or exclude
 * @returns {Promise<Object>} Updated section
 */
export async function toggleSection(draftId, sectionId, included) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/sections/${sectionId}/toggle`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ included }),
    }
  );
  return handleResponse(response);
}

/**
 * Reset section to AI original
 * @param {string} draftId - Draft ID
 * @param {string} sectionId - Section ID
 * @returns {Promise<Object>} Reset section with original content
 */
export async function resetSection(draftId, sectionId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/sections/${sectionId}/reset`,
    {
      method: 'POST',
      headers,
    }
  );
  return handleResponse(response);
}

/**
 * Regenerate AI commentary with custom guidelines
 * @param {string} draftId - Draft ID
 * @param {string} guidelines - User-edited guidelines text
 * @param {Object} options - Additional options
 * @param {Object} options.subjectAircraft - Subject aircraft details
 * @param {string[]} options.excludedSalesComps - Array of registration numbers to exclude from sales comps
 * @returns {Promise<Object>} Regenerated commentary { success, commentary, promptUsed, guidelinesUsed }
 */
export async function regenerateCommentary(draftId, guidelines, options = {}) {
  const headers = await getAuthHeaders();
  const body = { guidelines };

  if (options.subjectAircraft) {
    body.subjectAircraft = options.subjectAircraft;
  }
  if (options.excludedSalesComps !== undefined) {
    body.excludedSalesComps = options.excludedSalesComps;
  }

  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/regenerate-commentary`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
  );
  return handleResponse(response);
}

/**
 * Get default AI guidelines
 * @returns {Promise<Object>} Object with guidelines string
 */
export async function getDefaultGuidelines() {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts/default-guidelines`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * Refresh analysis with subject aircraft details
 * Uses the unified generateDraft endpoint with draftId to update existing draft
 * @param {string} draftId - Draft ID
 * @param {Object} params - Subject aircraft parameters
 * @param {string} params.subjectManufacturer - Optional manufacturer filter
 * @param {string} params.subjectModel - Optional model filter
 * @param {number} params.subjectYear - Optional year filter
 * @param {number} params.subjectHours - Optional hours context
 * @param {number} params.yearRange - Year range for filtering (default: 0)
 * @param {string} params.subjectAircraftDetails - Optional free text override
 * @param {string} params.customGuidelines - Optional custom AI guidelines
 * @param {string[]} params.excludedListings - Optional array of excluded registrations
 * @param {Object[]} params.editedComps - Optional array of edited comparable objects
 * @returns {Promise<Object>} Updated draft object
 */
export async function refreshAnalysis(draftId, params) {
  const headers = await getAuthHeaders();

  // Transform params to new unified endpoint format
  const requestBody = {
    draftId,
    subjectAircraft: {},
  };

  // Map old param names to new subjectAircraft structure
  if (params.subjectRegistration) {
    requestBody.subjectAircraft.registration = params.subjectRegistration;
  }
  if (params.subjectManufacturer) {
    requestBody.subjectAircraft.manufacturer = params.subjectManufacturer;
  }
  if (params.subjectModel) {
    requestBody.subjectAircraft.model = params.subjectModel;
  }
  if (params.subjectYear) {
    requestBody.subjectAircraft.year = params.subjectYear;
  }
  if (params.subjectHours) {
    requestBody.subjectAircraft.totalTime = params.subjectHours;
  }
  if (params.subjectAircraftDetails) {
    requestBody.subjectAircraft.additionalDetails = params.subjectAircraftDetails;
  }

  // Custom guidelines (passed to AI)
  if (params.customGuidelines) {
    requestBody.customGuidelines = params.customGuidelines;
  }

  // Excluded listings - always send if defined (empty array clears all exclusions)
  if (params.excludedListings !== undefined) {
    requestBody.excludedListings = params.excludedListings;
  }

  // Edited comps (user-curated on-market comparables)
  if (params.editedComps !== undefined) {
    requestBody.editedComps = params.editedComps;
  }

  // Excluded sales comps (registrations to exclude from sales analysis)
  if (params.excludedSalesComps !== undefined) {
    requestBody.excludedSalesComps = params.excludedSalesComps;
  }

  // analysisConfig - yearRange now inside (0 = exact year, null = all years)
  requestBody.analysisConfig = {
    periodDays: params.periodDays || 90,
    yearRange: params.yearRange === undefined ? 0 : params.yearRange,
  };

  const response = await apiFetch(`${API_BASE_URL}/generateDraft`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  const data = await handleResponse(response);

  // Return in format expected by SubjectAircraftPanel
  // The unified endpoint returns the full draft, so we extract relevant info
  return {
    success: true,
    draft: data,
    metrics: data.metrics,
    listingsCount: data.dataQuality ? {
      total: data.dataQuality.totalFetched,
      filtered: data.dataQuality.totalIncluded,
    } : undefined,
  };
}

/**
 * Get HTML preview of draft (PDF/print layout)
 * @param {string} draftId - Draft ID
 * @returns {Promise<string>} HTML preview content
 */
export async function getPreview(draftId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts/${draftId}/preview`, {
    headers,
  });
  if (!response.ok) {
    let errorMsg = 'Failed to load preview';
    try {
      const errorData = await response.json();
      errorMsg = errorData.error || errorMsg;
    } catch { /* response may not be JSON */ }
    throw new ReportApiError(errorMsg, response.status);
  }
  // Preview endpoint returns raw HTML, not JSON
  const html = await response.text();
  return html;
}

/**
 * Get shareable HTML preview of draft (responsive client view)
 * @param {string} draftId - Draft ID
 * @returns {Promise<string>} HTML preview content for client sharing
 */
export async function getShareablePreview(draftId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts/${draftId}/shareable-preview`, {
    headers,
  });
  if (!response.ok) {
    let errorMsg = 'Failed to load shareable preview';
    try {
      const errorData = await response.json();
      errorMsg = errorData.error || errorMsg;
    } catch { /* response may not be JSON */ }
    throw new ReportApiError(errorMsg, response.status);
  }
  // Returns raw HTML, not JSON
  const html = await response.text();
  return html;
}

/**
 * Finalize draft and generate PDF
 * @param {string} draftId - Draft ID
 * @returns {Promise<Object>} Finalization result with PDF URL
 */
export async function finalizeDraft(draftId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts/${draftId}/finalize`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  return handleResponse(response);
}

/**
 * List all drafts for the current user
 * @returns {Promise<Object>} Object with own and team arrays
 */
export async function listDrafts() {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/drafts`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * List all reports for the current user
 * @returns {Promise<Object>} Object with reports array
 */
export async function listReports() {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/reports`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * Get download URL for a finalized report
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Object with downloadUrl
 */
export async function downloadReport(reportId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/reports/${reportId}/download`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * Create or get existing share link for a report
 * @param {string} reportId - Report ID
 * @param {Object} options - Share options
 * @param {string} options.customerEmail - Optional customer email
 * @param {number} options.expiresInDays - Optional expiry in days (default: 30)
 * @returns {Promise<Object>} Share link details { shareId, shareUrl, expiresAt, customerName, existing, analytics? }
 */
export async function createShareLink(reportId, options = {}) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/reports/${reportId}/share`, {
    method: 'POST',
    headers,
    body: JSON.stringify(options),
  });
  return handleResponse(response);
}

/**
 * Get share link with analytics for a report
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Object with share (or null if none exists)
 */
export async function getShare(reportId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/reports/${reportId}/share`, {
    headers,
  });
  return handleResponse(response);
}

/**
 * Deactivate the share link for a report
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Confirmation
 */
export async function deactivateShare(reportId) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(`${API_BASE_URL}/reports/${reportId}/share`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
}

/**
 * Get source listings for a specific trend point
 * @param {string} draftId - Draft ID
 * @param {string} chartType - Chart type (inventory, price, etc.)
 * @param {string} date - Date string for the data point
 * @returns {Promise<Object>} Object with listings array
 */
export async function getSourceListings(draftId, chartType, date) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/source-listings?chartType=${encodeURIComponent(chartType)}&date=${encodeURIComponent(date)}`,
    {
      headers,
    }
  );
  return handleResponse(response);
}

/**
 * Regenerate draft with excluded listings
 * Uses the unified generateDraft endpoint with excludedListings parameter
 * @param {string} draftId - Draft ID
 * @param {Object} subjectAircraft - Subject aircraft details
 * @param {string[]} excludedListings - Array of registration numbers to exclude
 * @returns {Promise<Object>} Updated draft with recalculated metrics
 */
export async function regenerateWithExclusions(draftId, subjectAircraft, excludedListings) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/generateDraft`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        draftId,
        subjectAircraft,
        excludedListings,
      }),
    }
  );
  return handleResponse(response);
}

/**
 * Update hidden charts for a draft
 * @param {string} draftId - Draft ID
 * @param {string[]} hiddenCharts - Array of chart IDs to hide
 * @returns {Promise<Object>} Updated hidden charts { success, hiddenCharts }
 */
export async function updateHiddenCharts(draftId, hiddenCharts) {
  const headers = await getAuthHeaders();
  const response = await apiFetch(
    `${API_BASE_URL}/drafts/${draftId}/hidden-charts`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ hiddenCharts }),
    }
  );
  return handleResponse(response);
}

export { ReportApiError };
